import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { MagneticButton } from "@/components/magnetic-button";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { SiteFooter } from "@/components/site-footer";
import { PricingPlans } from "@/components/pricing-plans";

export const FAQS = [
  {
    q: "What does an app development consultant do?",
    a: "We sit on your side of the table and answer the questions you'd otherwise pay six figures to learn the hard way: what to build, what to skip, what stack, what tools, what the first three releases should be. Then we help you ship the first one.",
  },
  {
    q: "Is consulting the same as building?",
    a: "It can be either or both. Some clients hire us purely for the second opinion. Most ask us to build whatever we recommended. We're fine with either — we don't push the build.",
  },
  {
    q: "How is this different from a regular app development agency?",
    a: "A regular agency wants the contract. We want you to ship the right thing — even if that means we tell you not to build it at all. Our default mode is one-week engagements, not three-month projects.",
  },
  {
    q: "Do you consult on vibe-coded apps and AI tooling?",
    a: "That's most of our consulting work. Should you use Lovable or Cursor? When does the codebase need to be rebuilt by a human? Which AI tools are worth the subscription? We answer this every day.",
  },
];

export const Route = createFileRoute("/services/app-development-consultant")({
  head: () => ({
    meta: [
      { title: "Vibe Coding Consultant · App Strategy | Vibing Company" },
      {
        name: "description",
        content:
          "Senior advice on building with AI: which tool, what scope, what to ship next, and what will break in production. Then we help you build it.",
      },
      { property: "og:title", content: "App development consultant | the Vibing Company®" },
      ...ogImageMeta("services-app-development-consultant", "Share card: app development consultant"),
      {
        property: "og:description",
        content:
          "Senior app development consulting. Stack, scope, AI tooling, roadmap — then we help you ship it.",
      },
      { property: "og:url", content: "https://vibing.inc/services/app-development-consultant" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/services/app-development-consultant" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "App development consulting",
          serviceType: "App development consultant · senior product and engineering advisory",
          provider: { "@type": "Organization", name: "the Vibing Company", url: "https://vibing.inc/" },
          areaServed: "Worldwide",
          url: "https://vibing.inc/services/app-development-consultant",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Consultant,
});

function Consultant() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <When />
      <Format />
      <FAQ />
      <PricingPlans heading="When advice isn't enough" />
      <CTA />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="c-h" className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-16 md:pb-24">
      <h1 id="c-h" className="mt-8 font-display tracking-[-0.035em] leading-[1.02] max-w-[20ch]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
        Senior advice, no upsell.
      </h1>
      <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
        We're app development consultants for founders and product teams who want a senior second opinion before — or instead of — hiring an agency. Same people who'd build it, telling you whether you should.
      </p>
      <div className="mt-10 md:mt-14 flex flex-wrap gap-3">
        <TalkToJonButton variant="dark" immersive />
        <MagneticButton href="/services/mvp-development" variant="outline-dark">
          Or skip ahead to building
        </MagneticButton>
      </div>
    </section>
  );
}

const WHEN = [
  { k: "01", t: "You can't tell if the agency quote is reasonable", b: "Send us the proposal. We'll tell you what's fair, what's padded, and what's missing — in a 30-minute call." },
  { k: "02", t: "Lovable vs Cursor vs hire devs", b: "We use all of them. We'll tell you which one fits your stage, your skill, and your team — without the religion." },
  { k: "03", t: "The roadmap has 40 things and you have one quarter", b: "We help you cut. Not by voting — by talking to your users and reading your data." },
  { k: "04", t: "Something feels off in the codebase", b: "Code review with a written report. Security, architecture, performance, AI-readiness. One page, prioritised." },
];

function When() {
  return (
    <section aria-labelledby="w-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="w-h" className="fluid-h2 font-display max-w-[18ch]">The four conversations we have most weeks.</h2>
      <ul className="mt-14 grid md:grid-cols-2 gap-5">
        {WHEN.map((w) => (
          <li key={w.k} className="bg-background p-8 md:p-12">
            <div className="flex items-baseline gap-5">
              <span className="font-display text-electric leading-none" style={{ fontSize: "clamp(2.5rem, 4vw, 4rem)" }}>{w.k}</span>
              <h3 className="font-display tracking-[-0.03em] leading-[1.02]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>{w.t}</h3>
            </div>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-snug max-w-[44ch]">{w.b}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

const FORMATS = [
  { k: "Call", v: "30 or 60 minutes. You get a written follow-up the same day with the three things we'd do next." },
  { k: "Audit", v: "We read your codebase, your roadmap, your analytics. One-page report, prioritised, in 48 hours." },
  { k: "Embedded week", v: "We sit inside your team for five days. Ship one thing, advise on the next three." },
];

function Format() {
  return (
    <section aria-labelledby="f-h" className="relative border-t border-border/60 bg-secondary/40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <h2 id="f-h" className="fluid-h2 font-display max-w-[14ch]">Three sizes. Pick one.</h2>
        </div>
        <ul className="md:col-span-7 md:col-start-6 border-t border-foreground/20">
          {FORMATS.map((s) => (
            <li key={s.k} className="grid grid-cols-[100px_1fr] md:grid-cols-[160px_1fr] gap-6 items-baseline py-6 border-b border-foreground/20">
              <span className="font-display tracking-[-0.03em]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>{s.k}</span>
              <span className="text-base md:text-lg leading-snug">{s.v}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section aria-labelledby="faq-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="faq-h" className="fluid-h2 font-display max-w-[20ch]">App development consulting, answered.</h2>
      <dl className="mt-14 border-t border-border">
        {FAQS.map((f) => (
          <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
            <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">{f.q}</dt>
            <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">{f.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CTA() {
  return (
    <section aria-labelledby="cta-h" className="relative border-t border-border/60 bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2 id="cta-h" className="font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
            Bring the hard question.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[52ch] leading-snug">
             Bring the decision in front of you. Jon will give you a direct, senior perspective.
          </p>
        </div>
        <div className="md:col-span-4 md:col-start-9">
          <a href="https://cal.com/vibinc/30min" target="_blank" rel="noopener noreferrer" className="block group">
            <span className="mt-3 block font-display text-electric-bright break-all" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
              Talk to Jon<span aria-hidden className="inline-block ml-2 transition-transform group-hover:translate-x-2">↗</span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
